import { type ArtifactStatus, DRAFT_ARTIFACT_CHAIN, type DraftArtifactKind } from './artifacts.ts';
import { AUDIENCE_DRAFT_WAIT_MS } from './audience-draft.ts';
import type { DomainErrorCode } from './errors.ts';
import { DG_DRAFT_WAIT_MS } from './guide-draft.ts';
import type { StudyState } from './lifecycle.ts';
import type { LaunchMode } from './provisioning.ts';
import {
  POLICY_REFUSAL_COPY,
  POLICY_STEPS,
  type PolicyRefusal,
  type PolicyReviewOutcome,
} from './refusals.ts';
import { RQS_DRAFT_WAIT_MS } from './rqs.ts';
import { type StudyStepSlug, stepOfDraftKind } from './steps.ts';

/**
 * Why a study cannot be paid for yet, every reason at once and each with the step that fixes it.
 *
 * The purchase is the decision: `createOrResume` refuses the first thing wrong with a study, in its
 * own order, under the study row lock. This is the explanation of that decision, read for the
 * Launch step, and it asks the same predicates the purchase asks rather than a second opinion of
 * them — readiness, the stored policy review, the Quality floor, the simulation ceiling and the
 * panel catalogue. In an environment that can take a payment (a provider, and simulation mode), a
 * study it lists nothing for is one the purchase admits, and the first thing it lists is what the
 * purchase refuses with (`code`). Where it cannot, the purchase refuses `checkout_unavailable`
 * before it reads the study, and the list says so last: what the researcher can fix comes first,
 * in step order, and what nobody on the study can fix — the environment — comes once, at the end.
 */

/**
 * What one blocker is. Finer than `code`, which is the purchase's refusal: an unapproved guide and
 * a guide waiting on the research questions are both `study_not_ready` to the purchase, and
 * different things to do on Launch.
 *
 * - `materials_not_confirmed` — the Materials are not confirmed for the study's type.
 * - `step_not_approved` — the step's document is missing or not approved as it stands.
 * - `step_waiting_upstream` — the document is waiting on a change, and cannot be updated for it
 *   until a step above it is approved; the blocker names that step.
 * - `step_update_not_asked` — the document is waiting on a change that opening its step asks for.
 * - `step_updating` — Sigora is updating the document for the change now.
 * - `step_update_failed` — the update for the change could not be made; opening the step offers it
 *   again.
 * - `study_not_ready` — the study is not ready for a reason none of the above names.
 * - `study_locked` — the study is locked.
 * - `goal_not_chosen` — every other step is complete and the Goal is not chosen as it stands.
 * - `policy_refused` — the policy review refused the study; one blocker per refusal, at its step.
 * - `policy_review_pending`, `policy_review_required`, `policy_review_unavailable` — the review is
 *   being made, has not been asked for the study as it stands, or could not be had.
 * - `audience_below_quality_floor`, `study_too_large_to_simulate`, `audience_screener_invalid`,
 *   `audience_unresolvable` — the approved audience fails a check asked before payment.
 * - `audience_catalog_unavailable` — the panel could not be asked about the audience just now.
 * - `checkout_unavailable`, `audience_catalog_not_configured`, `audience_catalog_not_entitled` —
 *   the environment cannot take a payment or check an audience.
 */
export const LAUNCH_BLOCKER_KINDS = [
  'materials_not_confirmed',
  'step_not_approved',
  'step_waiting_upstream',
  'step_update_not_asked',
  'step_updating',
  'step_update_failed',
  'study_not_ready',
  'study_locked',
  'goal_not_chosen',
  'policy_refused',
  'policy_review_pending',
  'policy_review_required',
  'policy_review_unavailable',
  'audience_below_quality_floor',
  'study_too_large_to_simulate',
  'audience_screener_invalid',
  'audience_unresolvable',
  'audience_catalog_unavailable',
  'checkout_unavailable',
  'audience_catalog_not_configured',
  'audience_catalog_not_entitled',
] as const;
export type LaunchBlockerKind = (typeof LAUNCH_BLOCKER_KINDS)[number];

/**
 * What the researcher can do about a blocker: open its step, wait for Sigora, check again, or
 * nothing from this study.
 */
export const LAUNCH_BLOCKER_ACTIONS = ['open_step', 'wait', 'retry', 'none'] as const;
export type LaunchBlockerAction = (typeof LAUNCH_BLOCKER_ACTIONS)[number];

/**
 * One reason the study cannot be paid for: what it is, the code the purchase refuses with, the step
 * that fixes it (`null` for one no step fixes), what the researcher reads, and what they can do.
 */
export interface LaunchBlocker {
  kind: LaunchBlockerKind;
  code: DomainErrorCode;
  step: StudyStepSlug | null;
  message: string;
  action: LaunchBlockerAction;
}

/** Every code a blocker carries: the refusals a purchase answers that the list explains. */
export const LAUNCH_BLOCKER_CODES: ReadonlySet<string> = new Set<DomainErrorCode>([
  'study_not_ready',
  'study_locked',
  'goal_not_chosen',
  'study_refused',
  'policy_review_pending',
  'policy_review_required',
  'policy_review_unavailable',
  'audience_below_quality_floor',
  'study_too_large_to_simulate',
  'audience_screener_invalid',
  'audience_unresolvable',
  'audience_catalog_unavailable',
  'checkout_unavailable',
  'audience_catalog_not_configured',
  'audience_catalog_not_entitled',
]);

/**
 * How often Launch re-reads a pending policy review. It stops at `POLICY_REVIEW_WAIT_MS` from the
 * request, as the server stops counting the request as pending. A placeholder, as the steps' are.
 */
export const POLICY_REVIEW_POLL_MS = 2_000;

/** What a policy review means for the study now, as a list a wire schema can name. */
export const POLICY_REVIEW_OUTCOME_KINDS = [
  'allowed',
  'refused',
  'pending',
  'required',
  'unavailable',
] as const satisfies readonly PolicyReviewOutcome['kind'][];

/** A refusal a server-side check answered: its code, its sentence and its detail. */
export interface LaunchRefusal {
  code: DomainErrorCode;
  message: string;
  detail?: Readonly<Record<string, unknown>>;
}

export const LAUNCH_DRAFT_REQUEST_STATUSES = [
  'pending',
  'ready',
  'unreadable',
  'unavailable',
] as const;
export type LaunchDraftRequestStatus = (typeof LAUNCH_DRAFT_REQUEST_STATUSES)[number];

/**
 * One of the research questions, guide and audience as Launch reads it: the step view's own facts.
 *
 * - `status` — the document's status, or `null` while the study has none.
 * - `approvedCurrent` — approved at the version it stands at.
 * - `stale` — waiting on a change above it.
 * - `inputsKnown` — the study can say what the document would be updated from now (the step view's
 *   `currentInputs` is not `null`), which needs every step above it approved.
 * - `update` — the latest update request, only when it was asked for those inputs.
 */
export interface LaunchChainFact {
  status: ArtifactStatus | null;
  approvedCurrent: boolean;
  stale: boolean;
  inputsKnown: boolean;
  update: { status: LaunchDraftRequestStatus; requestedAt: string } | null;
}

/**
 * Everything the list is decided from, read in one snapshot of the study.
 *
 * - `purchase` — `held` for a pending or paid purchase, which the purchase answers with itself
 *   rather than refusing; `none` otherwise.
 * - `review` — the stored policy review held against the study as it stands under the policy in
 *   effect, or the Goal gap that stops one being asked.
 * - `audience` — the refusals the purchase's checks of the approved audience answer, in the order
 *   it asks them; `null` while the audience is not approved as it stands.
 * - `catalog` — what the panel catalogue answered, when it refuses at a purchase's strength.
 * - `checkout` — whether this process can take a payment at all, and the mode it launches in.
 */
export interface LaunchFacts {
  state: StudyState;
  purchase: 'none' | 'held';
  locked: boolean;
  materialsConfirmed: boolean;
  goalChosen: boolean;
  chain: Readonly<Record<DraftArtifactKind, LaunchChainFact>>;
  review: { kind: 'goal_not_chosen' } | { kind: 'outcome'; outcome: PolicyReviewOutcome };
  audience: readonly LaunchRefusal[] | null;
  catalog: LaunchRefusal | null;
  checkout: { available: boolean; mode: LaunchMode };
  now: Date;
}

/** What each step's document is called in a sentence: "Approve your guide." */
const DOCUMENT_NOUNS: Readonly<Record<StudyStepSlug, string>> = {
  study_type: 'study type',
  materials: 'materials',
  goal: 'goal',
  questions: 'questions',
  guide: 'guide',
  audience: 'audience',
  launch: 'launch',
  fielding: 'fielding',
  analyzing: 'brief',
  brief: 'brief',
};

/**
 * Every sentence the list says, in one place. The refused rows read `POLICY_REFUSAL_COPY`, and the
 * audience and catalogue rows the refusal's own sentence.
 */
export const LAUNCH_BLOCKER_COPY = {
  heading: 'Before you can launch',
  materialsNotConfirmed: 'Confirm your materials.',
  notApproved: (step: StudyStepSlug) => `Approve your ${DOCUMENT_NOUNS[step]}.`,
  waitingUpstream: (upstream: StudyStepSlug) =>
    upstream === 'materials'
      ? 'Confirm your materials first.'
      : upstream === 'goal'
        ? 'Choose your goal first.'
        : `Approve your ${DOCUMENT_NOUNS[upstream]} first.`,
  updating: (step: StudyStepSlug) =>
    `Sigora is updating your ${DOCUMENT_NOUNS[step]} for your changes.`,
  updateFailed: (step: StudyStepSlug) =>
    `Sigora couldn't update your ${DOCUMENT_NOUNS[step]}. Open it to try again.`,
  updateNotAsked: (step: StudyStepSlug) =>
    `Your ${DOCUMENT_NOUNS[step]} needs updating for your changes. Open it to update.`,
  studyNotReady: 'Approve every part of the study, with nothing waiting on a change.',
  studyLocked: 'This study is locked, so it can no longer be paid for here.',
  goalNotChosen: 'Choose your goal.',
  reviewPending: 'Sigora is checking your study. This usually takes under a minute.',
  reviewRequired: 'Sigora checks your study before you pay.',
  reviewUnavailable: "Sigora couldn't check your study just now. Try again shortly.",
  tooLargeToSimulate: (targetCompletes: number | null, maximum: number | null) =>
    targetCompletes !== null && maximum !== null
      ? `This study asks for ${String(targetCompletes)} people, and this environment can run at most ${String(maximum)}. Lower the sample size in the Audience card.`
      : 'This study asks for more people than this environment can run. Lower the sample size in the Audience card.',
  checkoutUnavailable: "Card payment isn't available yet.",
  /** The link on a blocker that names a step. */
  openStep: (stepName: string) => `Open ${stepName}`,
  /** The Launch step's own states around the list. */
  loading: 'Checking what is left to do…',
  loadFailed: "Sigora couldn't check what is left to do.",
  reviewOverdue: 'Sigora is taking longer than it should to check your study.',
  checkAgain: 'Check again',
  checkingAgain: 'Checking…',
} as const;

/** A refused verdict's row: the category's sentence, then the reviewer's guidance when it has one. */
export const policyRefusalBlockerMessage = (refusal: PolicyRefusal): string =>
  [POLICY_REFUSAL_COPY[refusal.category], refusal.guidance]
    .filter((part): part is string => part !== null && part !== '')
    .join(' ');

const UPDATE_WAIT_MS: Readonly<Record<DraftArtifactKind, number>> = {
  rqs: RQS_DRAFT_WAIT_MS,
  dg: DG_DRAFT_WAIT_MS,
  audience: AUDIENCE_DRAFT_WAIT_MS,
};

/**
 * Whether one document is part of a ready study: approved at the version it stands at, waiting on
 * nothing — which is also what lets a document below it be drafted from it.
 */
export const isLaunchChainReady = (fact: LaunchChainFact): boolean =>
  fact.status === 'approved' && fact.approvedCurrent && !fact.stale;

/**
 * The first step above `kind` that stops its update being asked: Materials not confirmed, the Goal
 * not chosen, or a document above it not approved as it stands. `null` when none does: a document
 * is never its own upstream.
 */
const upstreamOf = (facts: LaunchFacts, kind: DraftArtifactKind): StudyStepSlug | null => {
  if (!facts.materialsConfirmed) {
    return 'materials';
  }

  if (!facts.goalChosen) {
    return 'goal';
  }

  for (const above of DRAFT_ARTIFACT_CHAIN.slice(0, DRAFT_ARTIFACT_CHAIN.indexOf(kind))) {
    if (!isLaunchChainReady(facts.chain[above])) {
      return stepOfDraftKind(above);
    }
  }

  return null;
};

const STUDY_NOT_READY: LaunchBlocker = {
  kind: 'study_not_ready',
  code: 'study_not_ready',
  step: null,
  message: LAUNCH_BLOCKER_COPY.studyNotReady,
  action: 'none',
};

const notReady = (
  kind: LaunchBlockerKind,
  step: StudyStepSlug,
  message: string,
  action: LaunchBlockerAction = 'open_step',
): LaunchBlocker => ({ kind, code: 'study_not_ready', step, message, action });

/** What stops one document of the chain from being part of a ready study, if anything does. */
const chainBlocker = (facts: LaunchFacts, kind: DraftArtifactKind): LaunchBlocker | null => {
  const fact = facts.chain[kind];
  const step = stepOfDraftKind(kind);

  if (isLaunchChainReady(fact)) {
    return null;
  }

  if (fact.status === null || !fact.stale) {
    return notReady('step_not_approved', step, LAUNCH_BLOCKER_COPY.notApproved(step));
  }

  const upstream = fact.inputsKnown ? null : upstreamOf(facts, kind);
  if (upstream !== null) {
    return notReady(
      'step_waiting_upstream',
      upstream,
      LAUNCH_BLOCKER_COPY.waitingUpstream(upstream),
    );
  }

  const update = fact.update;
  if (
    update?.status === 'pending' &&
    facts.now.getTime() - Date.parse(update.requestedAt) < UPDATE_WAIT_MS[kind]
  ) {
    return notReady('step_updating', step, LAUNCH_BLOCKER_COPY.updating(step), 'wait');
  }

  if (update?.status === 'unavailable' || update?.status === 'unreadable') {
    return notReady('step_update_failed', step, LAUNCH_BLOCKER_COPY.updateFailed(step));
  }

  return notReady('step_update_not_asked', step, LAUNCH_BLOCKER_COPY.updateNotAsked(step));
};

const STEP_ORDER: readonly StudyStepSlug[] = POLICY_STEPS;

/** The policy review's rows, once the study is complete enough to be reviewed. */
const reviewBlockers = (review: LaunchFacts['review']): LaunchBlocker[] => {
  if (review.kind === 'goal_not_chosen') {
    return [
      {
        kind: 'goal_not_chosen',
        code: 'goal_not_chosen',
        step: 'goal',
        message: LAUNCH_BLOCKER_COPY.goalNotChosen,
        action: 'open_step',
      },
    ];
  }

  const { outcome } = review;
  switch (outcome.kind) {
    case 'allowed':
      return [];
    case 'refused':
      return outcome.refusals
        .toSorted((left, right) => STEP_ORDER.indexOf(left.step) - STEP_ORDER.indexOf(right.step))
        .map((refusal) => ({
          kind: 'policy_refused',
          code: 'study_refused',
          step: refusal.step,
          message: policyRefusalBlockerMessage(refusal),
          action: 'open_step',
        }));
    case 'pending':
      return [
        {
          kind: 'policy_review_pending',
          code: 'policy_review_pending',
          step: null,
          message: LAUNCH_BLOCKER_COPY.reviewPending,
          action: 'wait',
        },
      ];
    case 'required':
      return [
        {
          kind: 'policy_review_required',
          code: 'policy_review_required',
          step: null,
          message: LAUNCH_BLOCKER_COPY.reviewRequired,
          action: 'retry',
        },
      ];
    case 'unavailable':
      return [
        {
          kind: 'policy_review_unavailable',
          code: 'policy_review_unavailable',
          step: null,
          message: LAUNCH_BLOCKER_COPY.reviewUnavailable,
          action: 'retry',
        },
      ];
  }
};

const numberIn = (detail: LaunchRefusal['detail'], key: string): number | null => {
  const value = detail?.[key];

  return typeof value === 'number' ? value : null;
};

/** The approved audience's checks, as Audience-step rows. */
const AUDIENCE_CHECK_KINDS = new Set<LaunchBlockerKind>([
  'audience_below_quality_floor',
  'study_too_large_to_simulate',
  'audience_screener_invalid',
  'audience_unresolvable',
]);

const audienceBlocker = (refusal: LaunchRefusal): LaunchBlocker | null => {
  const kind = refusal.code as LaunchBlockerKind;
  if (!AUDIENCE_CHECK_KINDS.has(kind)) {
    return null;
  }

  return {
    kind,
    code: refusal.code,
    step: 'audience',
    message:
      kind === 'study_too_large_to_simulate'
        ? LAUNCH_BLOCKER_COPY.tooLargeToSimulate(
            numberIn(refusal.detail, 'target_completes'),
            numberIn(refusal.detail, 'maximum'),
          )
        : refusal.message,
    action: 'open_step',
  };
};

/**
 * Every reason the study cannot be paid for, in the order the researcher deals with them:
 *
 * 1. the Materials, not confirmed for the study's type;
 * 2. each of the research questions, guide and audience that is missing, unapproved or waiting on a
 *    change — split by what it waits on;
 * 3. the policy review, once 1–2 are clear: a Goal not chosen, each refusal at its step, or a
 *    review being made, not asked or not had;
 * 4. the approved audience's checks: the Quality floor, the simulation ceiling, its screening
 *    questions, whether the panel can find it, and a panel that could not be asked;
 * 5. the environment: no payment provider or a mode that cannot buy, and a catalogue that is not
 *    configured or does not admit the workspace.
 *
 * A study a purchase holds lists nothing: the purchase answers it with itself rather than refusing.
 */
export const launchBlockers = (facts: LaunchFacts): LaunchBlocker[] => {
  if (facts.purchase === 'held') {
    return [];
  }

  const blockers: LaunchBlocker[] = [];

  if (!facts.materialsConfirmed) {
    blockers.push(
      notReady('materials_not_confirmed', 'materials', LAUNCH_BLOCKER_COPY.materialsNotConfirmed),
    );
  }

  for (const kind of DRAFT_ARTIFACT_CHAIN) {
    const blocker = chainBlocker(facts, kind);
    if (blocker !== null) {
      blockers.push(blocker);
    }
  }

  const complete = blockers.length === 0;
  if (complete && facts.state !== 'ready') {
    blockers.push(STUDY_NOT_READY);
  }

  if (complete && facts.state === 'ready' && facts.locked) {
    blockers.push({
      kind: 'study_locked',
      code: 'study_locked',
      step: null,
      message: LAUNCH_BLOCKER_COPY.studyLocked,
      action: 'none',
    });
  }

  if (complete && !facts.locked) {
    blockers.push(...reviewBlockers(facts.review));
  }

  if (facts.audience !== null && !facts.locked) {
    for (const refusal of facts.audience) {
      const blocker = audienceBlocker(refusal);
      if (blocker !== null) {
        blockers.push(blocker);
      }
    }

    if (facts.catalog?.code === 'audience_catalog_unavailable') {
      blockers.push({
        kind: 'audience_catalog_unavailable',
        code: 'audience_catalog_unavailable',
        step: null,
        message: facts.catalog.message,
        action: 'retry',
      });
    }
  }

  if (!facts.checkout.available || facts.checkout.mode !== 'simulation') {
    blockers.push({
      kind: 'checkout_unavailable',
      code: 'checkout_unavailable',
      step: null,
      message: LAUNCH_BLOCKER_COPY.checkoutUnavailable,
      action: 'none',
    });
  }

  if (
    facts.catalog?.code === 'audience_catalog_not_configured' ||
    facts.catalog?.code === 'audience_catalog_not_entitled'
  ) {
    blockers.push({
      kind: facts.catalog.code,
      code: facts.catalog.code,
      step: null,
      message: facts.catalog.message,
      action: 'none',
    });
  }

  return blockers;
};

/** Whether a blocker is one the researcher can do something about on a step of the study. */
export const isFixableLaunchBlocker = (blocker: LaunchBlocker): boolean =>
  blocker.action !== 'none';
